import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { AppProvider, Icon } from "@shopify/polaris";
import {
  ProductListIcon,
  CashDollarIcon,
  BookOpenIcon,
  ConnectIcon,
  SettingsIcon,
  ClipboardChecklistIcon,
} from "@shopify/polaris-icons";
import PlanCard from "./Plan";
import Home from "./Home";
import HowToUse from "./HowToUse";
import Settings from "./Settings";
import DataTable3 from "./Components/DataTable3";
import ProductDetail from "./Components/ProductDetail/ProductDetail";
import GoogleShoppingCard from "./Components/GoogleShoppingCard";
import enTranslations from "@shopify/polaris/locales/en.json";
import StoreDetailSettings from "./StoreDetails/StoreDetailSettings";
import ShopifyStoreDetails from "./ShopifyStoreDetails/ShopifyStoreDetails";
import TabSettings from "./TabSettings";
import MerchantSelection from "./GoogleMerchantSelection";
import AdditionalCountries from "./AdditionalCountries/AdditionalCountries";
import SupportChat from "./Components/SupportChat/SupportChart";
import ProductCheckSettingsPage from "./ProductCheckSettingsPage";
import { buildEmbeddedPath, getShopifyContext } from "./utils/shopifyApp";
import { PlanProvider } from "./context/PlanContext";

const navigationItems = [
  {
    label: "Manage Products",
    path: "/settings",
    icon: ProductListIcon,
    match: ["/", "/settings", "/products", "/product-detail"],
  },
  { label: "Plan", path: "/plan", icon: CashDollarIcon, match: ["/plan"] },
  {
    label: "How to use",
    path: "/how-to-use",
    icon: BookOpenIcon,
    match: ["/how-to-use"],
  },
  {
    label: "Integrations",
    path: "/integrations",
    icon: ConnectIcon,
    match: ["/integrations", "/merchant-selection"],
  },
  {
    label: "Settings",
    path: "/tab-settings",
    icon: SettingsIcon,
    match: ["/tab-settings"],
  },
  {
    label: "Product Check",
    path: "/product-check",
    icon: ClipboardChecklistIcon,
    match: ["/product-check"],
  },
];

const planRequiredPaths = new Set([
  "/settings",
  "/",
  "/products",
  "/product-detail",
  "/integrations",
  "/tab-settings",
  "/merchant-selection",
  "/manage_additional_countries",
  "/product-check",
]);

const routeFeatureMap = {
  "/manage_additional_countries": "multi_country",
  "/product-check": "product_check",
};

function isNavItemActive(item, pathname) {
  return item.match.some((path) => {
    if (path === "/") return pathname === "/";
    return pathname === path || pathname.startsWith(`${path}/`);
  });
}

function AppNavigation({ items }) {
  const location = useLocation();
  const navigate = useNavigate();
  const navRef = useRef(null);

  useEffect(() => {
    const node = navRef.current;
    if (!node) return undefined;

    const syncNavHeight = () => {
      const height = Math.ceil(node.getBoundingClientRect().height);
      document.documentElement.style.setProperty(
        "--app-nav-height",
        `${height}px`,
      );
    };

    syncNavHeight();
    const observer = new ResizeObserver(syncNavHeight);
    observer.observe(node);
    window.addEventListener("resize", syncNavHeight);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", syncNavHeight);
    };
  }, [items.length]);

  return (
    <nav ref={navRef} className="app-shell__nav" aria-label="App sections">
      <div className="app-shell__nav-list">
        {items.map((item) => {
          const active = isNavItemActive(item, location.pathname);
          return (
            <button
              key={item.path}
              type="button"
              className={`app-shell__nav-link${active ? " is-active" : ""}`}
              onClick={() => navigate(buildEmbeddedPath(item.path))}
            >
              <span className="app-shell__nav-icon">
                <Icon source={item.icon} tone="base" />
              </span>
              <span>{item.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}

const App = () => {
  const [hasPlan, setHasPlan] = useState(null);
  const [activePlan, setActivePlan] = useState(null);

  useEffect(() => {
    const shop = getShopifyContext().shop;

    if (!shop) {
      setHasPlan(true);
      return;
    }

    const fetchPlanStatus = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/shopify/plan?shop=${shop}`,
        );
        const data = await response.json();
        setHasPlan(Boolean(data?.store?.plan_id));
        setActivePlan(data?.activePlan || null);
      } catch (error) {
        console.error("Error fetching plan status:", error);
        setHasPlan(true);
      }
    };

    fetchPlanStatus();
  }, []);

  const visibleNavigationItems = useMemo(() => {
    const base =
      hasPlan === false
        ? navigationItems.filter(
            (item) => item.path === "/plan" || item.path === "/how-to-use",
          )
        : navigationItems;

    return base.filter((item) => {
      const feature = routeFeatureMap[item.path];
      if (!feature) return true;
      if (hasPlan === false) return false;
      if (!activePlan) return true;
      return activePlan.features?.includes(feature);
    });
  }, [hasPlan, activePlan]);

  const withPlanGate = (path, element) => {
    if (hasPlan === false && planRequiredPaths.has(path)) {
      return <PlanCard />;
    }

    const requiredFeature = routeFeatureMap[path];

    if (
      requiredFeature &&
      hasPlan !== false &&
      activePlan &&
      !activePlan.features?.includes(requiredFeature)
    ) {
      return <PlanCard />;
    }

    return element;
  };

  const planContextValue = useMemo(
    () => ({
      hasPlan,
      activePlan,
      hasFeature: (featureKey) =>
        Boolean(activePlan?.features?.includes(featureKey)),
    }),
    [activePlan, hasPlan],
  );

  return (
    <AppProvider i18n={enTranslations}>
      <PlanProvider value={planContextValue}>
        <SupportChat />
        <BrowserRouter>
          <div className="app-shell">
            <AppNavigation items={visibleNavigationItems} />
            <Routes>
              <Route
                path="/settings"
                element={withPlanGate("/settings", <Settings />)}
              />
              <Route index path="/plan" element={<PlanCard />} />
              <Route path="/" element={withPlanGate("/", <Settings />)} />
              <Route path="/install" element={<Home />} />
              <Route path="/api/shopify/install" element={<Home />} />
              <Route path="/how-to-use" element={<HowToUse />} />
              <Route
                path="/products"
                element={withPlanGate("/products", <DataTable3 />)}
              />
              <Route
                path="/product-detail"
                element={withPlanGate("/product-detail", <ProductDetail />)}
              />
              <Route
                path="/integrations"
                element={withPlanGate("/integrations", <GoogleShoppingCard />)}
              />
              <Route
                path="/tab-settings"
                element={withPlanGate("/tab-settings", <TabSettings />)}
              />
              <Route
                path="/merchant-selection"
                element={withPlanGate(
                  "/merchant-selection",
                  <MerchantSelection />,
                )}
              />
              <Route
                path="/manage_additional_countries"
                element={withPlanGate(
                  "/manage_additional_countries",
                  <AdditionalCountries />,
                )}
              />
              <Route
                path="tab-settings/store-details"
                element={withPlanGate("/tab-settings", <StoreDetailSettings />)}
              />
              <Route
                path="tab-settings/shopify-settings"
                element={withPlanGate(
                  "/tab-settings",
                  <ShopifyStoreDetails />,
                )}
              />
              <Route
                path="/product-check"
                element={withPlanGate(
                  "/product-check",
                  <ProductCheckSettingsPage />,
                )}
              />
            </Routes>
          </div>
        </BrowserRouter>
      </PlanProvider>
    </AppProvider>
  );
};

export default App;
